/**
 * Universal rule numbers. Every tunable number lives here or in the ideology and slot data, so the balance
 * simulator (scripts/simulate.ts) can tune them without touching engine code. All values are small integers.
 */
import type { ClimateActionId, CrisisId, Forces, InvestArea, MissionId, ProgramId, UnitType } from '../../shared/types.ts';

/** Starting values before ideology and slot modifiers (design: base Treasury 20, Energy 10, Food 10, Index 50). */
export const BASE = {
  treasury: 20,
  energy: 10,
  food: 10,
  index: 50,
  cohesion: 3,
  forces: { army: 2, navy: 1, air: 1, defenses: 2 } satisfies Forces,
} as const;

/** Universal per-turn flows. Energy production = energyBase + Carbon + clean Energy + slot/ideology flows. */
export const FLOWS = {
  treasuryIncome: 7,
  energyBase: 3,
  energyUpkeep: 4,
  foodBase: 4,
  foodUpkeep: 4,
} as const;

export const LIMITS = {
  index: [0, 100] as const,
  support: [0, 100] as const,
  carbon: [0, 5] as const,
  resilience: [0, 3] as const,
  cohesion: [0, 5] as const,
  stock: 99,
  /** Units a nation may start building per turn. */
  buildsPerTurn: 3,
  missionsPerTurn: 2,
  programsPerTurn: 2,
  attacksPerTurn: 2,
  aidPerTurn: 2,
  /** Open trade proposals expire after this many turn resolutions. */
  proposalLifeTurns: 2,
  /** Maximum open proposals a nation may have out at once. */
  openProposals: 8,
  newsKept: 240,
  intelKept: 12,
} as const;

/** Shortfall penalties when a stock would fall below 0 after upkeep. */
export const SHORTFALL = {
  energyProsperity: -3,
  foodWellbeing: -4,
  foodSupport: -2,
  bankruptProsperity: -3,
} as const;

export const UNIVERSAL = {
  /** Credit downgrade: Treasury below this at end of turn costs Prosperity. */
  creditLine: 3,
  creditProsperity: -4,
  /** Stranded assets after the clean-tech tipping point. */
  strandedCarbon: 4,
  strandedProsperity: -1,
  /** National Pride: a nation named in a passed critical motion. */
  prideSupport: -2,
  /** Low Public Support causes protests. */
  protestLine: 30,
  protestProsperity: -1,
  protestSecurity: -1,
  /** Collapse: Public Support below this is counted as a government collapse. */
  collapseLine: 20,
  /** Net faction effect is clamped to +/- this per turn. */
  factionClamp: 6,
  /** Faction reactions are summed and halved (rounded toward zero) before the clamp. */
  factionDivisor: 2,
  /** Public Support drifts toward this level: 1 point per full 10 points away, per turn. */
  supportHome: 55,
  /** Each attack costs the attacker this much Public Support. */
  attackSupport: -3,
  leaveTreatySupport: -5,
  /** Tariff rule: the tariffing nation collects this per deal; each such deal yields 1 less unit to both sides. */
  tariffRevenue: 1,
  tariffTurns: 3,
  embargoIncome: -1,
  sanctionsIncome: -2,
  sanctionsTurns: 3,
  cohesionCrisisLine: 1,
  treatyCost: 1,
  tradeTreatyIncome: 1,
  tradeTreatyMaxCounted: 2,
  allianceSecurity: 3,
  /** Security gained per unit completed, lost per unit destroyed. */
  unitSecurity: 2,
  occupiedZoneTreasury: 1,
  occupiedZoneFood: 1,
  capitalLostSupport: -10,
} as const;

/** Environment is symmetric: E x (Turn 0 Carbon - current Carbon), plus adaptation and fair-share terms. */
/**
 * Rising expectations and recovery: each turn every index score moves 1 point toward `home` for every
 * full `per` points it is away (down when above, up when below). It treats every country profile alike,
 * keeps final scores near the pars and limits snowballing.
 */
export const INDEX_DRIFT = { home: 44, per: 5, spread: 10 } as const;

export const ENVIRONMENT = {
  perCarbon: 5,
  perResilience: 5,
  conservation: 5,
  fairShareMet: 3,
  perUnmitigatedHit: -3,
  stageEntry: -10,
} as const;

export const UNIT_COST: Record<UnitType, number> = { army: 3, navy: 4, air: 4, defenses: 2 };
/** Unit upkeep: 25% of the total cost of all units, rounded down. Building upkeep: 10% of program costs. */
export const UNIT_UPKEEP_PERCENT = 25;
export const PROGRAM_UPKEEP_PERCENT = 10;
export const UNIT_STRENGTH: Record<UnitType, number> = { army: 3, navy: 2, air: 3, defenses: 2 };

export const COMBAT = {
  /** Percent bonuses added to the defender's strength. */
  defenderBonus: 20,
  terrainBonus: 10,
  postureBonus: { defensive: 10, ready: 0, alert: 20 },
  allianceBonusEach: 10,
  allianceBonusMax: 30,
  umbrellaBonus: 30,
  militiaBonus: 20,
  /** Security lost by the loser of a battle on home soil, and by any nation attacked. */
  attackedSecurity: -3,
  lostZoneSecurity: -4,
  umbrellaAttackerSupport: -8,
  alertTreasury: 1,
} as const;

export interface ProgramData {
  id: ProgramId;
  name: string;
  cost: number;
  /** Local programs cost 1 less for Ironvale (State Governors) and Alvera (Subsidiarity). */
  regional: boolean;
  /** Only this ideology may build it; null for everyone. */
  only: string | null;
  maxLevel: number;
  effect: { prosperity?: number; security?: number; wellbeing?: number; environment?: number; cohesion?: number; support?: number };
  flows: { food?: number; energy?: number };
  text: string;
}

export const PROGRAMS: Record<ProgramId, ProgramData> = {
  health_service: {
    id: 'health_service', name: 'Health Service', cost: 4, regional: false, only: null, maxLevel: 1,
    effect: { wellbeing: 6 }, flows: {}, text: 'Clinics and nurses in every region. +6 Well-being.',
  },
  schools: {
    id: 'schools', name: 'Schools and Training', cost: 4, regional: true, only: null, maxLevel: 1,
    effect: { wellbeing: 3, prosperity: 3 }, flows: {}, text: 'Better schools and job training. +3 Well-being, +3 Prosperity.',
  },
  infrastructure: {
    id: 'infrastructure', name: 'Roads and Ports', cost: 5, regional: false, only: null, maxLevel: 1,
    effect: { prosperity: 6 }, flows: {}, text: 'New roads, rail and ports. +6 Prosperity.',
  },
  farm_support: {
    id: 'farm_support', name: 'Farm Support', cost: 3, regional: true, only: null, maxLevel: 1,
    effect: {}, flows: { food: 1 }, text: 'Seeds, water and tools for farmers. +1 Food every turn.',
  },
  energy_grid: {
    id: 'energy_grid', name: 'Clean Power Grid', cost: 3, regional: false, only: null, maxLevel: 1,
    effect: {}, flows: { energy: 1 }, text: 'A smarter grid with clean power. +1 Energy every turn, no Carbon.',
  },
  public_safety: {
    id: 'public_safety', name: 'Public Safety', cost: 3, regional: true, only: null, maxLevel: 1,
    effect: { security: 5 }, flows: {}, text: 'Trained police, firefighters and rescue teams. +5 Security.',
  },
  civic_inclusion: {
    id: 'civic_inclusion', name: 'Civic Inclusion', cost: 2, regional: true, only: null, maxLevel: 1,
    effect: { cohesion: 1, wellbeing: 2 }, flows: {}, text: 'Language classes and welcome centers. +1 Social Cohesion, +2 Well-being.',
  },
  community_charity: {
    id: 'community_charity', name: 'Community and Charity Network', cost: 2, regional: true, only: 'ironvale', maxLevel: 1,
    effect: { wellbeing: 2 }, flows: {},
    text: 'Charities, clubs and local groups ready to help. +2 Well-being, and crisis hits to Well-being are no longer 1 larger.',
  },
  civic_association: {
    id: 'civic_association', name: 'Civic Association Network', cost: 3, regional: false, only: 'meridia', maxLevel: 1,
    effect: { wellbeing: 2, support: 4 }, flows: {},
    text: 'Charities, clubs and firms organize help for hard times. +2 Well-being, +4 Public Support, and the drought and pandemic no longer cost double.',
  },
  workforce_renewal: {
    id: 'workforce_renewal', name: 'Workforce Renewal', cost: 3, regional: false, only: 'vellmark', maxLevel: 1,
    effect: {}, flows: {}, text: 'Training and support for new workers. Stops the aging-population upkeep rise.',
  },
  family_skills: {
    id: 'family_skills', name: 'Family and Skills', cost: 3, regional: false, only: 'alvera', maxLevel: 1,
    effect: {}, flows: {}, text: 'Family support and apprenticeships. Stops the aging-workforce Prosperity loss.',
  },
  workforce_training: {
    id: 'workforce_training', name: 'Workforce Training', cost: 3, regional: false, only: 'karsova', maxLevel: 1,
    effect: {}, flows: {}, text: 'Training programs for young workers. Stops the labor-shortage Prosperity loss.',
  },
  diversification: {
    id: 'diversification', name: 'Diversification', cost: 3, regional: false, only: 'aurem', maxLevel: 3,
    effect: { prosperity: 2 }, flows: {},
    text: 'New industries for the world after oil (3 levels). +2 Prosperity per level.',
  },
};

export interface InvestData {
  area: InvestArea;
  name: string;
  cost: number;
  effect: { prosperity?: number; wellbeing?: number; support?: number; security?: number };
  text: string;
}

export const INVEST: Record<InvestArea, InvestData> = {
  economy: { area: 'economy', name: 'Economic development', cost: 3, effect: { prosperity: 2 }, text: 'Loans and grants for new businesses. +2 Prosperity.' },
  services: { area: 'services', name: 'Public services', cost: 3, effect: { wellbeing: 2 }, text: 'More staff for clinics, schools and care. +2 Well-being.' },
  security: { area: 'security', name: 'Readiness drills', cost: 3, effect: { security: 2 }, text: 'Training for defense and rescue teams. +2 Security.' },
  outreach: { area: 'outreach', name: 'Town halls', cost: 2, effect: { support: 3 }, text: 'Leaders listen to citizens across the country. +3 Public Support.' },
};

export const MARKET = {
  /** The World Market buys up to this much Energy per nation per turn. */
  sellEnergyMax: 4,
  /** Energy per 1 Treasury when selling. 3 after the clean-tech tipping point (and for Aurem's oil decline). */
  sellRate: 2,
  sellRateLow: 3,
  buyEnergyMax: 4,
  buyEnergyPrice: 1,
  buyFoodMax: 3,
  buyFoodPrice: 2,
  /** The World Market also buys up to 4 Food per turn at 2 Food for 1 Treasury. */
  sellFoodMax: 4,
  sellFoodRate: 2,
  /** Up to half of a program's cost may be paid in Energy at this many Energy per Treasury. */
  energyPerTreasury: 2,
} as const;

export interface ClimateActionData {
  id: ClimateActionId;
  name: string;
  baseCost: number;
  needsTarget: boolean;
  only: string | null;
  onlySlots: string[] | null;
  text: string;
}

export const CLIMATE_ACTIONS: Record<ClimateActionId, ClimateActionData> = {
  green_transition: {
    id: 'green_transition', name: 'Green Transition', baseCost: 3, needsTarget: false, only: null, onlySlots: null,
    text: 'Switch power plants to clean energy. Carbon -1 from next turn, Environment +5, and -1 Energy this turn.',
  },
  double_green: {
    id: 'double_green', name: 'Green Industrial Drive', baseCost: 5, needsTarget: false, only: 'kestran', onlySlots: null,
    text: 'Two Green Transitions at once. Carbon -2, Environment +10, -2 Energy this turn.',
  },
  energy_innovation: {
    id: 'energy_innovation', name: 'Energy Innovation Act', baseCost: 4, needsTarget: false, only: 'ironvale', onlySlots: null,
    text: 'Next-generation nuclear and clean power. Carbon -1 with no Energy loss (at Carbon 0: +1 clean Energy every turn). May be given to another nation as a Clean Tech Transfer.',
  },
  expand_fossil: {
    id: 'expand_fossil', name: 'Expand Fossil Fuels', baseCost: 0, needsTarget: false, only: null, onlySlots: null,
    text: 'Open new oil, gas or coal projects. Carbon +1 (+1 Energy every turn), Environment -5. At most once every 2 turns.',
  },
  resilience: {
    id: 'resilience', name: 'Build Resilience', baseCost: 2, needsTarget: false, only: null, onlySlots: null,
    text: 'Sea walls, drainage and stronger buildings. Levels cost 2, 4 and 6. Each level cuts every climate hit by 1 and adds +5 Environment.',
  },
  early_warning: {
    id: 'early_warning', name: 'Early Warning', baseCost: 1, needsTarget: false, only: null, onlySlots: null,
    text: 'Storm alerts and evacuation plans. After this, a climate hit can never cut Public Support or Well-being by more than 1.',
  },
  heat_safe: {
    id: 'heat_safe', name: 'Heat-Safe Work', baseCost: 2, needsTarget: false, only: null, onlySlots: null,
    text: 'Shade, water and cooler work hours. Cancels the heat drain on Prosperity or Well-being.',
  },
  mangrove: {
    id: 'mangrove', name: 'Mangrove Belt', baseCost: 1, needsTarget: false, only: null, onlySlots: ['delta', 'atoll'],
    text: 'Plant mangrove forests along the coast. Every flood and storm-surge hit is 1 smaller.',
  },
  conservation: {
    id: 'conservation', name: 'Conservation', baseCost: 3, needsTarget: false, only: null, onlySlots: null,
    text: 'Protect wetlands, forests and sea grass that soak up carbon. +5 Environment and the Heat Meter -0.5. At most 2 per game.',
  },
  clean_tech_green: {
    id: 'clean_tech_green', name: 'Clean Tech Transfer', baseCost: 3, needsTarget: true, only: null, onlySlots: null,
    text: 'Pay for a Green Transition in another nation. It counts toward your climate fair share.',
  },
  clean_tech_resilience: {
    id: 'clean_tech_resilience', name: 'Adaptation Aid', baseCost: 2, needsTarget: true, only: null, onlySlots: null,
    text: 'Pay for the next Resilience level in another nation. It counts toward your climate fair share.',
  },
  innovation_prize: {
    id: 'innovation_prize', name: 'Innovation Prize', baseCost: 2, needsTarget: false, only: 'meridia', onlySlots: null,
    text: 'Offer a prize for cheaper clean tech. The next Green Transition by any nation costs 1 less, and Meridia gains +3 Prosperity when it is claimed. Counts 2 toward the fair share.',
  },
  forest_clearing: {
    id: 'forest_clearing', name: 'Clear Forest for Farms', baseCost: 0, needsTarget: false, only: null, onlySlots: ['canopy'],
    text: 'Clear rainforest for farms. +2 Food and +1 Treasury every turn, but Carbon +1 and Environment -8. At most twice per game.',
  },
};

export const CLIMATE_NUMBERS = {
  resilienceCosts: [2, 4, 6] as const,
  greenMinCost: 2,
  greenFloorAfterTipping: 1,
  conservationMax: 2,
  conservationMeter: -4,
  fossilEveryTurns: 2,
  forestMax: 2,
  forestFood: 2,
  forestTreasury: 1,
  forestExtraEnvironment: -3,
  innovationPrizeProsperity: 3,
  innovationPrizeFinance: 2,
  alveraExtraPerCarbon: 2,
  alveraConservation: 7,
} as const;

export interface MissionData {
  id: MissionId;
  name: string;
  cost: number;
  /** Base success percent before the target's defenses. */
  chance: number;
  text: string;
}

export const MISSIONS: Record<MissionId, MissionData> = {
  gather: { id: 'gather', name: 'Gather intelligence', cost: 1, chance: 90, text: 'Learn a nation’s Treasury, Energy, Food, Index and forces.' },
  counterintel: { id: 'counterintel', name: 'Counterintelligence', cost: 1, chance: 100, text: 'Guard your own country. Enemy missions are 25 points less likely to work, and failed spies are more likely to be caught.' },
  propaganda: { id: 'propaganda', name: 'Propaganda', cost: 2, chance: 50, text: 'Spread stories that cut the target’s Public Support by 3 and raise yours by 1.' },
  sabotage: { id: 'sabotage', name: 'Sabotage', cost: 2, chance: 40, text: 'Damage the target’s industry: -2 Treasury and -2 Prosperity.' },
  steal_tech: { id: 'steal_tech', name: 'Steal technology', cost: 3, chance: 20, text: 'Copy the target’s designs: +3 Prosperity and your next Green Transition costs 1 less.' },
};

export const INTEL = {
  counterintelDefense: 25,
  caughtOnFailure: 50,
  caughtWithCounterintel: 75,
  caughtAttackerSupport: -3,
  caughtTargetSupport: 2,
  minChance: 5,
  maxChance: 95,
  propagandaTarget: -3,
  propagandaSelf: 1,
  sabotageTreasury: -2,
  sabotageProsperity: -2,
  stealProsperity: 3,
} as const;

export interface CrisisResponseData {
  cost: number;
}

/** Cost to fund a crisis response (Interior). */
export const CRISIS_RESPONSE: Record<CrisisId, CrisisResponseData> = {
  pirates: { cost: 1 },
  drought: { cost: 2 },
  pandemic: { cost: 2 },
  island: { cost: 0 },
  refugees: { cost: 0 },
  flooding: { cost: 2 },
  terror: { cost: 1 },
};

/** Heat Meter (x8 integer scale). */
export const HEAT = {
  start: 240,
  stageThresholds: [0, 360, 520, 640] as const,
  dangerLine: 520,
  savePlanetLine: 520,
  savePlanetFinance8: 12,
  savePlanetFinanceSmall: 9,
  cleanTechDrop8: 6,
  methaneUnits: 16,
  diebackUnits: 24,
  diebackEnvironment: -20,
} as const;

/** Global goal lines (scoring.ts globalGoals; the text is built by globalGoalText in data/scoring.ts). */
export const GOALS = {
  /** Global Peace: no attack in the last this-many turns, and every nation ends with at least this Security. */
  peaceTurns: 5,
  peaceSecurity: 43,
  /** End Famine: no Food shortfall in the last this-many turns, every nation keeps this much Food, and the world
   * keeps at least `famineWorldFood8` Food in store in all in a world of 8 (scaled by n / 8, rounded up). */
  famineTurns: 5,
  famineFoodEach: 5,
  famineWorldFood8: 58,
  /** Contain the Pandemic: this share of nations must respond (rounded up), or the lower share under the Compact. */
  pandemicShare: 0.75,
  pandemicShareCompact: 0.625,
} as const;

export const SCORING = {
  goalPoints: 5,
  awardPoints: 3,
  objectivePoints: 5,
  verdictPoints: 10,
  /** Coalition against the leader: bots shun any nation this far above the world mean (displayed points). */
  leaderGap: 15,
} as const;

export const ABILITY_NUMBERS = {
  umbrellaTurns: 3,
  umbrellaSecurity: 3,
  summitWellbeing: 3,
  summitSecurity: 5,
  freePortTurns: 4,
  freePortTreasury: 1,
  planShiftMax: 6,
  accordTreasury: 4,
  accordProsperity: 5,
  accordFreezeTurns: 3,
  mediatorSecurityEach: 5,
  mediatorSecurityAlvera: 8,
  autonomyTurns: 3,
  autonomyTreasury: 2,
  visionCost: 8,
  visionProsperity: 10,
  visionDelay: 2,
  visionHalfOn: 3,
} as const;

/**
 * Bots and the Lonely Island (the one limited war a bot may start). An Opportunist tries to claim the unclaimed
 * island; later an Opportunist may fight the nation drilling there, when its odds are good enough, on or before
 * `lastWarTurn`. A nation that lost the island takes it back when its odds are good enough.
 */
export const AI_ISLAND = {
  claimChance: 60,
  warChance: 35,
  warOdds: 45,
  retakeOdds: 50,
  lastWarTurn: 16,
} as const;

/** Aurem's Royal Grants: Treasury paid each turn while the Crown pays them. */
export const ROYAL_GRANTS = 1;

/** Turns on which Kestran's Five-Year Plan may be used. */
export const FIVE_YEAR_PLAN_TURNS = [1, 5, 9, 13, 17] as const;
/** Ironvale's Tax-Cut Bet plan checks. */
export const PLAN_CHECK_TURNS = [4, 8, 12, 16, 20] as const;
/** Kestran and Aurem rotate the Head of State role at the start of these turns. */
export const ROTATION_TURNS = [6, 11, 16] as const;
